import { useState, useCallback, useEffect } from 'react';
import { isExpiredSessionError } from '@/api/types';
import {
  getActivities,
  getCurrentPatientActivities,
  getActivityById,
  getCurrentPatientActivityById,
  downloadActivities
} from '@/api/activities';
import type { Activity, ActivityFilters, DownloadActivityRequest } from '@/types/activity';
import { downloadBlob, generateDownloadFilename } from '@/utils/download';
import { useToast } from '@/contexts/toastContextBase';

interface UseActivitiesOptions {
  patientId?: string;
  initialFilters?: ActivityFilters;
  isCurrentPatient?: boolean; // Set to true if viewing as current patient
}

export function useActivities(
  options: UseActivitiesOptions = {}
) {
  const { patientId, initialFilters, isCurrentPatient = false } = options;

  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<ActivityFilters>(initialFilters || {});
  const { showToast } = useToast();

  const fetchActivities = useCallback(async () => {
    // For current patient viewing their own activities
    if (isCurrentPatient) {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getCurrentPatientActivities(filters);
        setActivities(response.activities);
      } catch (err) {
        if (isExpiredSessionError(err)) {
          return;
        }

        const message = err instanceof Error ? err.message : 'Failed to load activities';
        setError(new Error(message));
        showToast('error', message);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // For doctors viewing patient activities
    if (!patientId) {
      setActivities([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getActivities(patientId, filters);
      setActivities(response.activities);
    } catch (err) {
      if (isExpiredSessionError(err)) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Failed to load activities';
      setError(new Error(message));
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, filters, isCurrentPatient, showToast]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const updateFilters = useCallback((newFilters: Partial<ActivityFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  return {
    activities,
    isLoading,
    error,
    filters,
    updateFilters,
    clearFilters,
    refetch: fetchActivities,
  };
}

interface UseActivityOptions {
  activityId?: string;
  patientId?: string;
  isCurrentPatient?: boolean;
}

export function useActivity(
  options: UseActivityOptions = {}
) {
  const { activityId, patientId, isCurrentPatient = false } = options;

  const [activity, setActivity] = useState<Activity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!activityId) {
      setActivity(null);
      setIsLoading(false);
      return;
    }

    // For current patient viewing their own activity
    if (isCurrentPatient) {
      const fetchActivity = async () => {
        setIsLoading(true);
        setError(null);

        try {
          const data = await getCurrentPatientActivityById(activityId);
          if (!data) {
            throw new Error('Activity not found');
          }
          setActivity(data);
        } catch (err) {
          if (isExpiredSessionError(err)) {
            return;
          }

          const message = err instanceof Error ? err.message : 'Failed to load activity';
          setError(new Error(message));
          showToast('error', message);
        } finally {
          setIsLoading(false);
        }
      };

      fetchActivity();
      return;
    }

    // For doctors viewing patient activity
    if (!patientId) {
      setActivity(null);
      setIsLoading(false);
      return;
    }

    const fetchActivity = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getActivityById(activityId, patientId);
        if (!data) {
          throw new Error('Activity not found');
        }
        setActivity(data);
      } catch (err) {
        if (isExpiredSessionError(err)) {
          return;
        }

        const message = err instanceof Error ? err.message : 'Failed to load activity';
        setError(new Error(message));
        showToast('error', message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivity();
  }, [activityId, patientId, isCurrentPatient, showToast]);

  return { activity, isLoading, error };
}

export function useActivityDownload() {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showToast } = useToast();

  const download = useCallback(
    async (request: DownloadActivityRequest) => {
      setIsDownloading(true);

      try {
        const blob = await downloadActivities(request);
        const filename = generateDownloadFilename('activities', 'json');
        downloadBlob(blob, filename);
        showToast('success', 'Download started');
      } catch (err) {
        if (isExpiredSessionError(err)) {
          throw err;
        }

        const message = err instanceof Error ? err.message : 'Download failed';
        showToast('error', message);
        throw err;
      } finally {
        setIsDownloading(false);
      }
    },
    [showToast]
  );

  return { download, isDownloading };
}
